import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Check,
  Clock,
  CloudUpload,
  Gamepad2,
  Info,
  ListOrdered,
  Lock,
  MapPin,
  RefreshCw,
  Route,
  Smartphone,
  Unplug,
  type LucideIcon,
} from 'lucide-react';
import {
  AUTO_INTERVALS,
  AUTO_SYNC_NOTE,
  AUTO_SYNC_OFF_BODY,
  DRIVE_BENEFITS,
  DRIVE_DISCONNECT_BODY,
  DRIVE_FREQUENCIES,
  DRIVE_PRIVACY,
  DRIVE_WHAT_BODY,
  SAMPLE_BACKUP,
  SYNC_HINT,
  autoSyncSummary,
  backupSummary,
  cloneSnapshot,
  driveBadge,
  isAutoInterval,
  isDriveFrequency,
  lastBackupText,
  pendingCount,
  sectionHeading,
  statusCaption,
  statusTitle,
  visibleSections,
  type AutoInterval,
  type BackupSyncSnapshot,
  type DriveFrequency,
  type SyncSection,
  type SyncSectionId,
} from '../../data/backup-sync-mock';
import { BackupArt } from './BackupArt';
import './backup.css';

const ACTION_MS = 480;

const SECTION_ICON: Record<SyncSectionId, LucideIcon> = {
  locations: MapPin,
  playlists: ListOrdered,
  itineraries: Route,
  joystick: Gamepad2,
};

const BENEFIT_ICON: Record<(typeof DRIVE_BENEFITS)[number]['id'], LucideIcon> = {
  secure: CloudUpload,
  anywhere: Smartphone,
  auto: Clock,
};

export interface BackupSyncViewProps {
  /**
   * Leave unset to preview skeletons once.
   * Pass a real flag once sync and Drive APIs are wired.
   */
  loading?: boolean;
  /**
   * Leave unset to use the built-in sample.
   * Pass the account payload once it exists. An empty pending list shows the illustration.
   * Keep the object reference stable, or each new object replaces local edits.
   */
  snapshot?: BackupSyncSnapshot;
  /** Signed-in email. Used only with the built-in sample. */
  accountEmail?: string;
}

type Busy = 'sync' | 'backup' | null;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), 680);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function pause(): Promise<void> {
  if (prefersReducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    window.setTimeout(resolve, ACTION_MS);
  });
}

function seedSnapshot(snapshot: BackupSyncSnapshot | undefined, accountEmail: string): BackupSyncSnapshot {
  const next = cloneSnapshot(snapshot ?? SAMPLE_BACKUP);
  if (!snapshot && accountEmail) {
    next.drive.email = accountEmail;
  }
  return next;
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`bk-skel ${className}`} />;
}

function StatusSkeleton() {
  return (
    <div className="bk-card bk-hero" data-testid="skeleton-status" aria-hidden="true">
      <div className="bk-hero__row">
        <Skel className="bk-skel--ico" />
        <div className="bk-skel__stack">
          <Skel className="bk-skel--title" />
          <Skel className="bk-skel--line" />
        </div>
        <Skel className="bk-skel--btn" />
      </div>
      <div className="bk-hero__chips">
        <Skel className="bk-skel--chip" />
        <Skel className="bk-skel--chip" />
        <Skel className="bk-skel--chip" />
      </div>
    </div>
  );
}

function PendingSkeleton() {
  return (
    <div className="bk-card bk-pad" data-testid="skeleton-pending" aria-hidden="true">
      <Skel className="bk-skel--title" />
      <Skel className="bk-skel--line" />
      <div className="bk-skel__rows">
        {[0, 1, 2].map((key) => (
          <Skel key={key} className="bk-skel--row" />
        ))}
      </div>
    </div>
  );
}

function AutoSkeleton() {
  return (
    <div className="bk-card bk-pad" data-testid="skeleton-auto" aria-hidden="true">
      <div className="bk-hero__row">
        <Skel className="bk-skel--ico" />
        <div className="bk-skel__stack">
          <Skel className="bk-skel--title" />
          <Skel className="bk-skel--line" />
        </div>
        <Skel className="bk-skel--switch" />
      </div>
      <Skel className="bk-skel--row" />
      <Skel className="bk-skel--row" />
    </div>
  );
}

function DriveSkeleton() {
  return (
    <div className="bk-card bk-pad" data-testid="skeleton-drive" aria-hidden="true">
      <div className="bk-hero__row">
        <Skel className="bk-skel--ico" />
        <div className="bk-skel__stack">
          <Skel className="bk-skel--title" />
          <Skel className="bk-skel--short" />
        </div>
        <Skel className="bk-skel--chip" />
      </div>
      <div className="bk-drive__grid">
        <Skel className="bk-skel--block" />
        <div className="bk-skel__rows">
          <Skel className="bk-skel--row" />
          <Skel className="bk-skel--row" />
        </div>
      </div>
    </div>
  );
}

function DriveMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="bk-drive-mark">
      <path fill="#34A853" d="M8.2 20.2 3.2 12h6.2l4.8 8.2z" />
      <path fill="#FBBC04" d="M15.6 20.2 10.6 12h9.8l-4.8 8.2z" />
      <path fill="#4285F4" d="M12 12.2 8.2 4.2h7.6L12 12.2z" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <span className="bk-g" aria-hidden="true">
      G
    </span>
  );
}

const BackupSyncView: React.FC<BackupSyncViewProps> = ({
  loading: loadingProp,
  snapshot,
  accountEmail = '',
}) => {
  const autoLoading = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? autoLoading;
  const freqName = useId();
  const intervalName = useId();
  const dialogTitleId = useId();
  const dialogBodyId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const alive = useRef(true);
  const [state, setState] = useState(() => seedSnapshot(snapshot, accountEmail));
  const [busy, setBusy] = useState<Busy>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    setState(seedSnapshot(snapshot, accountEmail));
    setBusy(null);
    setDisconnectOpen(false);
    setNotice('');
  }, [snapshot, accountEmail]);

  useEffect(() => {
    if (!disconnectOpen) return undefined;
    const node = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const items = () =>
      Array.from(
        node?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea') ?? []
      ).filter((item) => !item.hasAttribute('disabled'));
    items()[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDisconnectOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !node) return;
      const focusable = items();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [disconnectOpen]);

  const count = pendingCount(state.pending);
  const sections = visibleSections(state.pending);
  const canRestore = snapshot === undefined;
  const badge = driveBadge(state.drive, busy === 'backup');

  const syncNow = async () => {
    if (busy || pendingCount(state.pending) === 0) return;
    setBusy('sync');
    await pause();
    if (!alive.current) return;
    setState((current) => ({
      ...current,
      pending: current.pending.map((section) => ({ ...section, items: [] })),
      lastSyncedLabel: 'Just now',
      syncError: '',
    }));
    setBusy(null);
    setNotice('Synchronisation completed successfully!');
  };

  const backupNow = async () => {
    if (busy || !state.drive.connected) return;
    setBusy('backup');
    setState((current) => ({ ...current, drive: { ...current.drive, error: '' } }));
    await pause();
    if (!alive.current) return;
    setState((current) => ({
      ...current,
      drive: {
        ...current.drive,
        lastBackupLabel: 'Just now',
        recent: true,
        error: '',
      },
    }));
    setBusy(null);
    setNotice('Backup complete. Your MockLoc data is safe in Google Drive.');
  };

  const connectDrive = async () => {
    if (busy || state.drive.connected) return;
    const email = state.drive.email || accountEmail || 'user@gmail.com';
    setBusy('backup');
    setState((current) => ({
      ...current,
      drive: {
        ...current.drive,
        connected: true,
        email,
        error: '',
      },
    }));
    await pause();
    if (!alive.current) return;
    setState((current) => ({
      ...current,
      drive: {
        ...current.drive,
        connected: true,
        email: current.drive.email || email,
        lastBackupLabel: 'Just now',
        recent: true,
        error: '',
      },
    }));
    setBusy(null);
    setNotice('Backup complete. Your MockLoc data is safe in Google Drive.');
  };

  const confirmDisconnect = () => {
    setState((current) => ({
      ...current,
      drive: {
        ...current.drive,
        connected: false,
        error: '',
      },
    }));
    setDisconnectOpen(false);
    setNotice('Google Drive disconnected. Your local MockLoc data is unchanged.');
  };

  const chooseInterval = (value: string) => {
    if (!isAutoInterval(value)) return;
    setState((current) => ({ ...current, autoInterval: value }));
  };

  const chooseFrequency = (value: string) => {
    if (!isDriveFrequency(value) || busy === 'backup') return;
    setState((current) => ({
      ...current,
      drive: { ...current.drive, frequency: value },
    }));
  };

  return (
    <div className="bk" data-testid="backup-screen" aria-busy={loading}>
      {loading ? (
        <>
          <p className="visually-hidden" role="status">
            Loading backup and sync
          </p>
          <StatusSkeleton />
          <div className="bk-split">
            <PendingSkeleton />
            <AutoSkeleton />
          </div>
          <DriveSkeleton />
        </>
      ) : (
        <>
          {notice ? (
            <p className="bk-toast" role="status">
              {notice}
            </p>
          ) : null}

          <section
            className={`bk-card bk-hero${count > 0 ? ' is-pending' : ' is-synced'}`}
            aria-labelledby="backup-status-title"
          >
            <div className="bk-hero__row">
              <span className={`bk-badge${count > 0 ? ' is-pending' : ' is-synced'}`} aria-hidden="true">
                {count > 0 ? <CloudUpload size={22} /> : <Check size={22} />}
              </span>
              <div className="bk-hero__copy">
                <h2 id="backup-status-title">{statusTitle(count)}</h2>
                <p>{statusCaption(state, count)}</p>
              </div>
              {count > 0 && (
                <button
                  type="button"
                  className="bk-sync"
                  onClick={() => {
                    void syncNow();
                  }}
                  disabled={busy != null}
                >
                  <RefreshCw size={16} aria-hidden="true" className={busy === 'sync' ? 'is-spin' : ''} />
                  {busy === 'sync' ? 'Syncing…' : 'Sync now'}
                </button>
              )}
            </div>

            {count > 0 && (
              <ul className="bk-chips">
                {sections.map((section) => (
                  <li key={section.id}>
                    <strong>{section.items.length}</strong>
                    {section.title}
                  </li>
                ))}
              </ul>
            )}

            {busy === 'sync' && (
              <div
                className="bk-progress"
                role="progressbar"
                aria-label="Syncing MockLoc data"
              >
                <i />
              </div>
            )}

            {state.syncError ? (
              <div className="bk-alert" role="alert">
                <p>
                  <strong>Backup failed</strong>
                  {state.syncError}
                </p>
                <div className="bk-alert__actions">
                  <button type="button" onClick={() => { void syncNow(); }}>
                    Retry
                  </button>
                  <button
                    type="button"
                    aria-label="Dismiss error"
                    onClick={() => setState((current) => ({ ...current, syncError: '' }))}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          <div className="bk-split">
            {count === 0 ? (
              <section className="bk-card bk-empty" data-testid="backup-empty">
                <BackupArt variant="pending" />
                <h2>Nothing waiting to sync</h2>
                <p>
                  Locations, playlists, itineraries, and joystick sessions that still need a backup
                  will show up here.
                </p>
                {canRestore && (
                  <button
                    type="button"
                    className="bk-restore"
                    onClick={() => {
                      setState(seedSnapshot(undefined, accountEmail));
                      setNotice('Sample changes restored.');
                    }}
                  >
                    Show sample changes
                  </button>
                )}
              </section>
            ) : (
              <PendingList sections={sections} count={count} />
            )}

            <AutoSyncCard
              enabled={state.autoSyncEnabled}
              interval={state.autoInterval}
              intervalName={intervalName}
              onToggle={() =>
                setState((current) => ({
                  ...current,
                  autoSyncEnabled: !current.autoSyncEnabled,
                }))
              }
              onInterval={chooseInterval}
            />
          </div>

          {state.drive.connected ? (
            <DriveConnected
              email={state.drive.email}
              lastBackup={lastBackupText(state.drive.lastBackupLabel)}
              badgeLabel={badge.label}
              badgeTone={badge.tone}
              frequency={state.drive.frequency}
              freqName={freqName}
              error={busy === 'backup' ? '' : state.drive.error}
              backingUp={busy === 'backup'}
              onFrequency={chooseFrequency}
              onBackup={() => {
                void backupNow();
              }}
              onRetry={() => {
                void backupNow();
              }}
              onDisconnect={() => setDisconnectOpen(true)}
            />
          ) : (
            <DriveConnect
              busy={busy != null}
              onConnect={() => {
                void connectDrive();
              }}
            />
          )}
        </>
      )}

      {disconnectOpen && (
        <div className="bk-modal">
          <div
            ref={dialogRef}
            className="bk-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            aria-describedby={dialogBodyId}
          >
            <h2 id={dialogTitleId}>Disconnect Google Drive?</h2>
            <p id={dialogBodyId}>{DRIVE_DISCONNECT_BODY}</p>
            <div className="bk-dialog__actions">
              <button type="button" className="bk-ghost" onClick={() => setDisconnectOpen(false)}>
                Cancel
              </button>
              <button type="button" className="bk-danger" onClick={confirmDisconnect}>
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function PendingList({ sections, count }: { sections: SyncSection[]; count: number }) {
  return (
    <section className="bk-card bk-pending" aria-labelledby="backup-pending-title">
      <header className="bk-pending__head">
        <h2 id="backup-pending-title">{backupSummary(count)}</h2>
        <p>{SYNC_HINT}</p>
      </header>
      {sections.map((section) => {
        const Icon = SECTION_ICON[section.id];
        return (
          <div key={section.id} className={`bk-group bk-group--${section.id}`}>
            <h3>
              <span className="bk-group__icon" aria-hidden="true">
                <Icon size={16} />
              </span>
              {sectionHeading(section)}
            </h3>
            <ul>
              {section.items.map((item) => (
                <li key={item.id}>{item.label}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}

function AutoSyncCard({
  enabled,
  interval,
  intervalName,
  onToggle,
  onInterval,
}: {
  enabled: boolean;
  interval: AutoInterval;
  intervalName: string;
  onToggle: () => void;
  onInterval: (value: string) => void;
}) {
  return (
    <section className="bk-card bk-auto" aria-labelledby="backup-auto-title">
      <div className="bk-auto__top">
        <span className="bk-badge is-auto" aria-hidden="true">
          <RefreshCw size={20} />
        </span>
        <div>
          <h2 id="backup-auto-title">Auto sync in background</h2>
          <p>Upload locations and playlists while the app is closed</p>
        </div>
        <button
          type="button"
          className="bk-switch"
          role="switch"
          aria-checked={enabled}
          aria-label="Auto sync in background"
          onClick={onToggle}
        >
          <i />
        </button>
      </div>

      {enabled ? (
        <>
          <p className="bk-auto__summary">{autoSyncSummary(interval)}</p>
          <fieldset className="bk-choices">
            <legend>Check interval</legend>
            {AUTO_INTERVALS.map((option) => (
              <label
                key={option.id}
                className={`bk-choice${interval === option.id ? ' is-on' : ''}`}
              >
                <input
                  type="radio"
                  name={intervalName}
                  value={option.id}
                  checked={interval === option.id}
                  onChange={() => onInterval(option.id)}
                />
                <span className="bk-choice__copy">
                  <span>{option.label}</span>
                  <small>{option.detail}</small>
                  {option.recommended ? <em>Recommended</em> : null}
                </span>
                <span className="bk-choice__mark" aria-hidden="true" />
              </label>
            ))}
          </fieldset>
          <div className="bk-note">
            <span className="bk-note__icon" aria-hidden="true">
              <Info size={18} />
            </span>
            <div>
              <h3>How background sync works</h3>
              <p>{AUTO_SYNC_NOTE}</p>
            </div>
          </div>
        </>
      ) : (
        <div className="bk-off">
          <h3>Keep your data backed up automatically</h3>
          <p>{AUTO_SYNC_OFF_BODY}</p>
          <p className="bk-off__hint">
            Use the switch above to enable background sync and set your preferred interval.
          </p>
        </div>
      )}
    </section>
  );
}

function DriveConnected({
  email,
  lastBackup,
  badgeLabel,
  badgeTone,
  frequency,
  freqName,
  error,
  backingUp,
  onFrequency,
  onBackup,
  onRetry,
  onDisconnect,
}: {
  email: string;
  lastBackup: string;
  badgeLabel: string;
  badgeTone: 'good' | 'warn' | 'danger';
  frequency: DriveFrequency;
  freqName: string;
  error: string;
  backingUp: boolean;
  onFrequency: (value: string) => void;
  onBackup: () => void;
  onRetry: () => void;
  onDisconnect: () => void;
}) {
  return (
    <section className="bk-drive" aria-label="Google Drive Backup">
      <div className="bk-card bk-drive-on">
        <div className="bk-drive-on__row">
          <span className="bk-drive-logo" aria-hidden="true">
            <DriveMark />
          </span>
          <div>
            <h2>Connected to Google Drive</h2>
            <p>{email || 'Google account'}</p>
          </div>
          <span className="bk-drive-on__meta">
            <span className={`bk-pill is-${badgeTone}`}>{badgeLabel}</span>
            {badgeTone === 'good' ? (
              <span className="bk-check" aria-hidden="true">
                <Check size={14} />
              </span>
            ) : null}
          </span>
        </div>
        <p className="bk-drive-on__when">{lastBackup}</p>
      </div>

      <div className="bk-drive__grid">
        <section className="bk-card" aria-labelledby="backup-freq-title">
          <div className="bk-section-head">
            <span className="bk-badge is-drive" aria-hidden="true">
              <Clock size={18} />
            </span>
            <div>
              <h2 id="backup-freq-title">Backup Frequency</h2>
              <p>Choose how often you want to back up your data.</p>
            </div>
          </div>
          <fieldset className="bk-choices">
            <legend className="visually-hidden">Backup frequency</legend>
            {DRIVE_FREQUENCIES.map((option) => (
              <label
                key={option.id}
                className={`bk-choice${frequency === option.id ? ' is-on' : ''}`}
              >
                <input
                  type="radio"
                  name={freqName}
                  value={option.id}
                  checked={frequency === option.id}
                  disabled={backingUp}
                  onChange={() => onFrequency(option.id)}
                />
                <span className="bk-choice__copy">
                  <span>{option.label}</span>
                  {option.recommended ? <em>Recommended</em> : null}
                </span>
                <span className="bk-choice__mark" aria-hidden="true" />
              </label>
            ))}
          </fieldset>
        </section>

        <div className="bk-drive__stack">
          <button
            type="button"
            className="bk-card bk-action"
            onClick={onBackup}
            disabled={backingUp}
          >
            <span className="bk-badge is-drive" aria-hidden="true">
              <CloudUpload size={18} />
            </span>
            <span>
              <strong>Back Up Now</strong>
              <small>{backingUp ? 'Backing up…' : lastBackup}</small>
            </span>
            {backingUp ? (
              <span className="bk-spinner" aria-hidden="true" />
            ) : null}
          </button>

          <div className="bk-card bk-drive-info">
            <span className="bk-badge is-drive" aria-hidden="true">
              <Info size={18} />
            </span>
            <div>
              <h2>What gets backed up?</h2>
              <p>{DRIVE_WHAT_BODY}</p>
            </div>
          </div>

          <button type="button" className="bk-card bk-disconnect" onClick={onDisconnect} disabled={backingUp}>
            <span className="bk-badge is-danger" aria-hidden="true">
              <Unplug size={18} />
            </span>
            <span>
              <strong>Disconnect Google Drive</strong>
              <small>This will stop future backups. Your existing backups in Google Drive will remain.</small>
            </span>
          </button>

          {error ? (
            <button type="button" className="bk-error" onClick={onRetry}>
              <span>{error}</span>
              <strong>Retry</strong>
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function DriveConnect({ busy, onConnect }: { busy: boolean; onConnect: () => void }) {
  return (
    <section className="bk-card bk-connect" data-testid="drive-disconnected" aria-labelledby="drive-connect-title">
      <BackupArt variant="drive" />
      <h2 id="drive-connect-title">Keep your MockLoc data safe</h2>
      <p className="bk-connect__lead">
        Connect your Google Drive to automatically backup your mocks, playlists, settings and more.
      </p>
      <ul className="bk-benefits">
        {DRIVE_BENEFITS.map((benefit) => {
          const Icon = BENEFIT_ICON[benefit.id];
          return (
            <li key={benefit.id} className={`bk-benefit bk-benefit--${benefit.id}`}>
              <span aria-hidden="true">
                <Icon size={18} />
              </span>
              <span>
                <strong>{benefit.title}</strong>
                <small>{benefit.body}</small>
              </span>
            </li>
          );
        })}
      </ul>
      <button type="button" className="bk-connect__cta" onClick={onConnect} disabled={busy}>
        <GoogleMark />
        Connect to Google Drive
      </button>
      <p className="bk-privacy">
        <Lock size={14} aria-hidden="true" />
        {DRIVE_PRIVACY}
      </p>
    </section>
  );
}

export default BackupSyncView;
